import { useState, useMemo, useCallback, useEffect } from 'react';
import type { FilterGroup } from '@practium/ui';
import { useToast } from '@practium/ui';
import { fuzzyFilter, formatDate } from '@practium/shared';
import { useAuth } from '@practium/features/auth';
import { CATEGORY_LABELS } from '../types/productRequest.types';
import type {
	ClientProductRequestTab,
	ProductRequestFormData,
	RequestProductStatus,
} from '../types/productRequest.types';
import { buildUnionDateRange, isInDateRange } from '../../../shared/utils/dateFilter.utils';
import { ProductRequestSchema } from '../schemas/productRequest.schema';
import { useGetRequestProducts, usePostRequestProducts } from '@practium/features/api';
import { RequestProduct } from '@practium/features/products/types';

const PAGE_SIZE = 10;

export const FILTER_GROUPS: FilterGroup[] = [
	{
		label: 'Categoria',
		options: [
			{ value: 'categoria:fertilizer', label: CATEGORY_LABELS.fertilizer },
			{ value: 'categoria:lime', label: CATEGORY_LABELS.lime },
			{ value: 'categoria:conditioner', label: CATEGORY_LABELS.conditioner },
			{ value: 'categoria:organic', label: CATEGORY_LABELS.organic },
			{ value: 'categoria:special', label: CATEGORY_LABELS.special },
			{ value: 'categoria:defensive', label: CATEGORY_LABELS.defensive },
		],
	},
	{
		label: 'Período',
		options: [
			{ value: 'periodo:15', label: 'Últimos 15 dias' },
			{ value: 'periodo:30', label: 'Últimos 30 dias' },
			{ value: 'periodo:custom', label: 'Personalizado' },
		],
	},
];

export const CATEGORY_OPTIONS = [
	{ value: 'defensive', label: CATEGORY_LABELS.defensive },
	{ value: 'fertilizer', label: CATEGORY_LABELS.fertilizer },
	{ value: 'lime', label: CATEGORY_LABELS.lime },
	{ value: 'conditioner', label: CATEGORY_LABELS.conditioner },
	{ value: 'organic', label: CATEGORY_LABELS.organic },
	{ value: 'special', label: CATEGORY_LABELS.special },
];

const STATUS_TAB_MAP: Record<Exclude<ClientProductRequestTab, 'all'>, RequestProductStatus> = {
	pending: 'PENDING',
	approved: 'APPROVED',
	rejected: 'REJECTED',
};

const EMPTY_FORM: ProductRequestFormData = {
	name: '',
	manufacturer: '',
	category: '',
	note: '',
};

export const useProductRequests = () => {
	const toast = useToast();
	const { user } = useAuth();
	const [requests, setRequests] = useState<RequestProduct[]>([]);
	const [activeTab, setActiveTab] = useState<ClientProductRequestTab>('pending');
	const [selectedRequest, setSelectedRequest] = useState<RequestProduct | null>(null);
	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [formData, setFormData] = useState<ProductRequestFormData>(EMPTY_FORM);
	const [formErrors, setFormErrors] = useState<Record<string, string>>({});
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [customDateFrom, setCustomDateFrom] = useState('');
	const [customDateTo, setCustomDateTo] = useState('');
	const [searchQuery, setSearchQuery] = useState('');
	const [sortAsc, setSortAsc] = useState(false);
	const [page, setPage] = useState(1);

	const currentDate = formatDate(new Date());
	const userName = user?.name ?? '';

	const { data, isLoading } = useGetRequestProducts();
	const { mutateAsync: postRequestProduct } = usePostRequestProducts();


	useEffect(() => {
		if (data) {
			setRequests(data);
		}
	}, [data]);




	useEffect(() => {
		setPage(1);
	}, [activeTab, filterValues, customDateFrom, customDateTo, searchQuery]);

	const processedRequests = useMemo(() => {
		let result = requests;

		if (activeTab !== 'all') {
			result = result.filter((r) => r.status === STATUS_TAB_MAP[activeTab]);
		}

		result = fuzzyFilter(result, searchQuery, (r) =>
			[r.name, r.manufacturer, r.category].join(' ')
		);

		const categoryFilters = filterValues
			.filter((v) => v.startsWith('categoria:'))
			.map((v) => v.slice(10));
		if (categoryFilters.length > 0) {
			result = result.filter((r) => categoryFilters.includes(r.category));
		}

		const periodoFilters = filterValues
			.filter((v) => v.startsWith('periodo:'))
			.map((v) => v.slice(8));
		if (periodoFilters.length > 0) {
			const { from, to } = buildUnionDateRange(periodoFilters, customDateFrom, customDateTo);
			if (from || to) result = result.filter((r) => isInDateRange(r.createdAt, from, to));
		}

		if (sortAsc) {
			result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
		}

		return result;
	}, [requests, activeTab, filterValues, customDateFrom, customDateTo, searchQuery, sortAsc]);

	const totalPages = useMemo(
		() => Math.max(1, Math.ceil(processedRequests.length / PAGE_SIZE)),
		[processedRequests.length]
	);

	const paginatedRequests = useMemo(
		() => processedRequests.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
		[processedRequests, page]
	);

	const setField = useCallback(
		<K extends keyof ProductRequestFormData>(field: K, value: ProductRequestFormData[K]) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
		},
		[]
	);

	const clearError = useCallback((field: string) => {
		setFormErrors((prev) => {
			if (!prev[field]) return prev;
			return { ...prev, [field]: '' };
		});
	}, []);

	const handleOpenCreate = useCallback(() => {
		setFormData(EMPTY_FORM);
		setFormErrors({});
		setIsCreateOpen(true);
	}, []);

	const handleCloseCreate = useCallback(() => {
		setFormErrors({});
		setIsCreateOpen(false);
	}, []);

	const handleSubmit = useCallback(async () => {
		const result = ProductRequestSchema.safeParse(formData);
		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setFormErrors(fieldErrors);
			return;
		}

		try {
			const newRequest = await postRequestProduct({
				name: result.data.name,
				manufacturer: result.data.manufacturer,
				category: result.data.category,
				note: result.data.note ?? '',
			});
			setRequests((prev) => [newRequest, ...prev]);
			setIsCreateOpen(false);
			setFormData(EMPTY_FORM);
			setFormErrors({});
			toast.success('Solicitação enviada com sucesso!');
		} catch {
			toast.error('Erro ao enviar a solicitação.');
		}
	}, [formData, user, currentDate, toast]);

	const handleCustomDateChange = useCallback((from: string, to: string) => {
		setCustomDateFrom(from);
		setCustomDateTo(to);
	}, []);

	return {
		requests,
		isLoading,
		activeTab,
		setActiveTab,
		selectedRequest,
		setSelectedRequest,
		isCreateOpen,
		formData,
		setField,
		formErrors,
		clearError,
		filterValues,
		setFilterValues,
		customDateFrom,
		customDateTo,
		handleCustomDateChange,
		searchQuery,
		setSearchQuery,
		sortAsc,
		setSortAsc,
		page,
		setPage,
		paginatedRequests,
		totalPages,
		currentDate,
		userName,
		handleOpenCreate,
		handleCloseCreate,
		handleSubmit,
	};
};
